import React from 'react';
import { Box, Typography } from '@mui/material';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieLabelRenderProps,
} from 'recharts';
import { TotalDataItem } from '@/types';

// Color palette matching the reference image style
const COLORS = [
    '#1976d2', // Primary blue
    '#d32f2f', // Red
    '#388e3c', // Green
    '#f57c00', // Orange
    '#7b1fa2', // Purple
    '#00796b', // Teal
    '#c2185b', // Pink
    '#512da8', // Deep purple
];

interface MerchantTransactionPieChartProps {
    data: TotalDataItem[];
}

const MerchantTransactionLineChart: React.FC<
    MerchantTransactionPieChartProps
> = ({ data }) => {
    // Aggregate all values
    const totalChargebacks = data.reduce(
        (acc, item) => acc + Number(item.chargebackFee),
        0
    );
    const totalTransactions = data.reduce(
        (acc, item) => acc + Number(item.transactionAmount),
        0
    );
    const totalRefunds = data.reduce(
        (acc, item) => acc + Number(item.refundAmount) + Number(item.refundFee),
        0
    );
    const totalReserveFees = data.reduce(
        (acc, item) => acc + Number(item.reserveFee),
        0
    );
    const totalPayoutCharges = data.reduce(
        (acc, item) => acc + Number(item.payoutserviceCharge),
        0
    );
    const totalSstgFees = data.reduce(
        (acc, item) => acc + Number(item.sstgFee),
        0
    );
    const totalAchAmount = data.reduce(
        (acc, item) => acc + Number(item.achAmount),
        0
    );
    const totalIcAmount = data.reduce(
        (acc, item) => acc + Number(item.icAmount),
        0
    );

    const pieData = [
        {
            name: 'Successful Transactions',
            value: totalTransactions,
            amount: totalTransactions,
        },
        {
            name: 'Chargebacks',
            value: totalChargebacks,
            amount: totalChargebacks,
        },
        {
            name: 'Refunds',
            value: totalRefunds,
            amount: totalRefunds,
        },
        {
            name: 'Reserve Fees',
            value: totalReserveFees,
            amount: totalReserveFees,
        },
        {
            name: 'Payout Service Charges',
            value: totalPayoutCharges,
            amount: totalPayoutCharges,
        },
        {
            name: 'SSTG Fees',
            value: totalSstgFees,
            amount: totalSstgFees,
        },
        {
            name: 'ACH Amount',
            value: totalAchAmount,
            amount: totalAchAmount,
        },
        {
            name: 'IC Amount',
            value: totalIcAmount,
            amount: totalIcAmount,
        },
    ].filter(item => item.value > 0); // Only show items with positive values

    // Custom tooltip formatter
    const formatTooltip = (value: number, name: string) => {
        return [`$${value.toFixed(2)}`, name];
    };

    if (!pieData?.length)
        return (
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Typography
                    textAlign="center"
                    variant="body2"
                    color="textSecondary"
                >
                    No transaction and fee data available for this period.
                </Typography>
            </Box>
        );

    return (
        <Box sx={{ width: '100%', height: 400 }}>
            <Typography
                variant="h6"
                sx={{
                    textAlign: 'center',
                    fontWeight: 'bold',
                    mb: 2,
                    fontSize: '1.1rem',
                }}
            >
                Transaction and Fee Distribution
            </Typography>

            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="45%"
                        innerRadius={60}
                        outerRadius={100}
                        fill="#8884d8"
                        paddingAngle={2}
                        label={(props: PieLabelRenderProps) => {
                            const percent =
                                typeof props.percent === 'number'
                                    ? props.percent
                                    : 0;
                            return `${(percent * 100).toFixed(1)}%`;
                        }}
                        // label={({ name, percent }) =>
                        //     `${(percent * 100).toFixed(1)}%`
                        // }
                        labelLine={false}
                    >
                        {pieData.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                                stroke="#fff"
                                strokeWidth={2}
                            />
                        ))}
                    </Pie>
                    <Tooltip
                        formatter={formatTooltip}
                        contentStyle={{
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                    />
                    <Legend
                        layout="vertical"
                        verticalAlign="middle"
                        align="right"
                        wrapperStyle={{
                            paddingLeft: '20px',
                            fontSize: '14px',
                        }}
                        iconSize={12}
                        iconType="circle"
                    />
                </PieChart>
            </ResponsiveContainer>
        </Box>
    );
};

export default MerchantTransactionLineChart;
